import { useCallback } from 'react'
import { awardPoints, notifyCardCompleted } from '../../hooks/useKanbifyData'
import { awardProcedureBonus } from '../../hooks/useDashboardV2'
import { kanbify } from '../../lib/kanbifyClient'
import {
    buildUserCompletionPayload,
    computeCompletionAward,
} from '../../lib/cardCompletion'
import { STEP_KPI_ENABLED } from './cardDetailUtils'

export default function useCardCompletionFlow({
    t,
    card,
    user,
    profile,
    companyMembers,
    readOnly,
    effectiveIsCompleted,
    effectiveTimerStartedAt,
    setCompletionOverride,
    cardActionPending,
    setCardActionPending,
    setTimerOverride,
    setUpdateError,
    setShowCompletionModal,
    normalizedCompletionPayload,
    completionFields,
    cardProcedures,
    checklists,
    isProcedureCard,
    persistActiveTimer,
    onUpdate,
    onRefreshCards,
    emitDashboardCardState,
    showAchievement,
}) {
    const doneForCurrentUser = effectiveIsCompleted
    const configLocked = readOnly || !!effectiveTimerStartedAt || doneForCurrentUser

    const saveCompletionPayloadForUser = useCallback(async (values) => {
        if (!user?.id) return { success: false, error: t('card.kpi.userMissing') }

        const { payload: nextPayload, submittedAt } = buildUserCompletionPayload({
            payload: normalizedCompletionPayload,
            userId: user.id,
            fields: completionFields,
            values,
        })

        const result = await onUpdate(card.id, {
            completion_payload: nextPayload,
            completion_submitted_at: submittedAt,
            completion_submitted_by: user.id,
        })

        if (result?.success === false) {
            return { success: false, error: result.error || t('card.kpi.errorSave') }
        }

        return { success: true, payload: nextPayload }
    }, [card?.id, completionFields, normalizedCompletionPayload, onUpdate, user?.id, t])

    const finalizeCardCompletion = useCallback(async ({ completionValues = null } = {}) => {
        if (cardActionPending) return { success: false, error: 'busy' }
        setCardActionPending('complete')
        let optimisticCompletion = false

        const awardForMyCompletion = async () => {
            try {
                let pts
                let reason
                let kpiValues = null
                if (completionFields.length > 0) {
                    const vals = (completionValues
                        || normalizedCompletionPayload.by_user?.[user.id]?.values
                        || {})
                    const award = computeCompletionAward(card, vals)
                    pts = award.points
                    reason = award.reason
                    kpiValues = award.kpiValues
                } else {
                    const award = computeCompletionAward(card)
                    pts = award.points
                    reason = award.reason
                    kpiValues = award.kpiValues
                }
                pts = Math.max(0, Math.round(pts))
                if (pts > 0) {
                    await awardPoints(user.id, pts, reason, card.id, true, false)
                    window.dispatchEvent(new CustomEvent('kanbify:points-refresh'))
                }
                const procId = cardProcedures[0]?.procedure_id
                if (procId) {
                    await kanbify.from('procedure_completions').insert([{
                        procedure_id: procId,
                        card_id: card.id,
                        user_id: user.id,
                        company_id: profile?.company_id || null,
                        points_earned: pts,
                        kpi_values: kpiValues,
                        completed_at: new Date().toISOString(),
                    }])
                }
            } catch { /* non-fatal - completion already persisted */ }
        }

        try {
            const completingNow = !effectiveIsCompleted
            if (completionValues && completionFields.length > 0) {
                const payloadResult = await saveCompletionPayloadForUser(completionValues)
                if (!payloadResult.success) {
                    setUpdateError(payloadResult.error || t('card.kpi.errorSave'))
                    return { success: false, error: payloadResult.error }
                }
            }

            if (completingNow) {
                setCompletionOverride({ cardId: card.id, value: true })
                optimisticCompletion = true
                const timerResult = await persistActiveTimer()
                if (timerResult?.success === false) {
                    setCompletionOverride({ cardId: card.id, value: effectiveIsCompleted })
                    optimisticCompletion = false
                    return timerResult
                }
            }

            const newStatus = !effectiveIsCompleted
            const result = await onUpdate(card.id, { is_completed: newStatus })
            if (result?.success !== false) {
                setCompletionOverride({ cardId: card.id, value: newStatus })
                if (newStatus) {
                    await awardForMyCompletion()
                    setTimerOverride({ cardId: card.id, value: null })
                    emitDashboardCardState('completed', {
                        is_completed: true,
                        timer_started_at: null,
                        updated_at: new Date().toISOString(),
                    })
                    void notifyCardCompleted({
                        cardId: card.id,
                        cardTitle: card.title,
                        recipientIds: [
                            ...(companyMembers || []).filter((member) => member.is_active !== false).map((member) => member.id),
                            card.created_by,
                        ],
                        actorId: user.id,
                        senderName: profile?.full_name,
                        companyId: profile?.active_company_id || profile?.company_id,
                    })
                    void (async () => {
                        try {
                            const pauseCutoff = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()
                            const { data: lastLogs } = await kanbify
                                .from('time_logs')
                                .select('card_id, created_at')
                                .eq('user_id', user.id)
                                .neq('card_id', card.id)
                                .gte('created_at', pauseCutoff)
                                .order('created_at', { ascending: false })
                                .limit(20)
                            const candidateId = (lastLogs || []).find((row) => row.card_id)?.card_id
                            if (!candidateId) return
                            const { data: candidate } = await kanbify
                                .from('cards')
                                .select('id, title, is_completed, timer_started_at, card_members!inner(user_id)')
                                .eq('id', candidateId)
                                .eq('card_members.user_id', user.id)
                                .maybeSingle()
                            if (!candidate || candidate.is_completed || candidate.timer_started_at) return
                            const { data: resumeResult } = await kanbify.rpc('resume_paused_card', { p_card_id: candidateId })
                            if (resumeResult?.success) {
                                emitDashboardCardState('started', { timer_started_at: new Date().toISOString(), is_completed: false })
                                showAchievement({ type: 'success', message: t('dashboard.toast.autoResumed', { defaultValue: `Am reluat „${candidate.title}"`, title: candidate.title }) })
                            }
                        } catch { /* best-effort */ }
                    })()
                } else {
                    setTimerOverride({ cardId: card.id, value: null })
                    emitDashboardCardState('reopened', {
                        is_completed: false,
                        timer_started_at: null,
                        updated_at: new Date().toISOString(),
                    })
                }
                onRefreshCards()
                return { success: true }
            }

            if (optimisticCompletion) {
                setCompletionOverride({ cardId: card.id, value: effectiveIsCompleted })
            }
            setUpdateError(result?.error || t('card.field.couldNotSaveCheckConnection'))
            return { success: false, error: result?.error }
        } finally {
            setCardActionPending(null)
        }
    }, [card, cardActionPending, companyMembers, completionFields, normalizedCompletionPayload, cardProcedures, effectiveIsCompleted, emitDashboardCardState, onRefreshCards, onUpdate, persistActiveTimer, saveCompletionPayloadForUser, showAchievement, user?.id, profile?.full_name, profile?.active_company_id, profile?.company_id, t, setCardActionPending, setCompletionOverride, setTimerOverride, setUpdateError])

    const handleCompleteFromDetail = useCallback(async () => {
        setUpdateError('')
        const alreadyDoneForMe = effectiveIsCompleted
        if (STEP_KPI_ENABLED && isProcedureCard && !alreadyDoneForMe) {
            const stepDoneOf = (item) => item.is_shared
                ? (item.completions || []).some((completion) => completion.user_id === user?.id)
                : !!item.is_completed
            const anyStepDone = (checklists || []).some((checklist) =>
                (checklist.checklist_items || checklist.items || []).some(stepDoneOf))
            if (!anyStepDone) {
                showAchievement({ type: 'error', message: t('card.step.completeFirst') })
                return
            }
            await awardProcedureBonus(card.id)
            window.dispatchEvent(new CustomEvent('kanbify:points-refresh'))
            await finalizeCardCompletion()
            return
        }
        if (!alreadyDoneForMe && completionFields.length > 0) {
            setShowCompletionModal(true)
            return
        }
        await finalizeCardCompletion()
    }, [card?.id, isProcedureCard, checklists, user?.id, completionFields.length, effectiveIsCompleted, finalizeCardCompletion, showAchievement, t, setShowCompletionModal, setUpdateError])

    return {
        doneForCurrentUser,
        configLocked,
        finalizeCardCompletion,
        handleCompleteFromDetail,
    }
}
